import { useContext, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { TenantConfigContext } from 'context/TenantConfigContext';
import { useActivateTenant } from 'services/onbarding.service';
import { PoweredByTbOneFooter } from 'components/PoweredByTbOneFooter';
import { Button } from 'components/Button';

export type CenteredPageType = 'NEW' | 'ACTIVATING';

interface CenteredPageProps {
  pageType: CenteredPageType;
}

interface PageConfiguration {
  headlineId: string;
  descriptionId: string;
  footerId?: string;
  buttonId?: string;
}

export function ActivatingTenantPage({ pageType }: CenteredPageProps) {
  const activateTenant = useActivateTenant();
  const { updateActivationStatus } = useContext(TenantConfigContext);
  const [isActivating, setIsActivating] = useState(false);

  const pageConfigurations: Record<CenteredPageType, PageConfiguration> = {
    NEW: {
      headlineId: 'activation.new.headline',
      descriptionId: 'activation.new.description',
      footerId: 'activation.new.footer',
      buttonId: 'activation.new.button',
    },
    ACTIVATING: {
      headlineId: 'activation.ongoing.headline',
      descriptionId: 'activation.ongoing.description',
    },
  };

  const activeConfig = pageConfigurations[pageType];

  const handleActivate = () => {
    if (isActivating) return;

    setIsActivating(true);

    activateTenant()
      .then(response => {
        if (response?.error) {
          console.error(response.error instanceof Error ? response.error.message : String(response.error));
          updateActivationStatus('NEW');
        } else {
          updateActivationStatus(response?.data ?? 'ACTIVATING');
        }
      })
      .catch((error: unknown) => {
        console.error(error instanceof Error ? error.message : String(error));
        updateActivationStatus('NEW');
      })
      .finally(() => {
        setIsActivating(false);
      });
  };

  return (
    <div className="flex flex-col block-list h-screen">
      <div
        style={{ maxWidth: 1128 }}
        className="flex flex-col justify-center items-center block-list gap-y-10 mx-auto my-auto text-center w-full break-words whitespace-pre-line"
      >
        <img src="/tradebyte/tb360/tb-360-logo-black.svg" alt="TB.360 Logo" width="197px" className="block" />

        <p className="font-normal text-5xl text-gray-900">
          <FormattedMessage id={activeConfig.headlineId} />
        </p>

        <p className="text-lg text-gray-700">
          <FormattedMessage id={activeConfig.descriptionId} />
        </p>

        {activeConfig.footerId && (
          <span className="font-semibold text-gray-900">
            <FormattedMessage id={activeConfig.footerId} />
          </span>
        )}

        {activeConfig.buttonId && (
          <Button disabled={isActivating} size="md" onClick={handleActivate}>
            <FormattedMessage id={activeConfig.buttonId} />
          </Button>
        )}
      </div>

      <div className="mt-auto">
        <PoweredByTbOneFooter />
      </div>
    </div>
  );
}
